import React from "react";
import Moment from "react-moment";
import { Building2, MapPin, Clock, CheckCircle2, XCircle } from "lucide-react";
import "./ViewFacility.css";

const sectionTitleStyle = {
  fontSize: "12px",
  fontWeight: 700,
  color: "#94A3B8",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  borderBottom: "1px solid #EEF2F6",
  paddingBottom: "8px",
  marginBottom: "14px",
};

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: 700,
  color: "#2E6DA4",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: "4px",
};

const valueStyle = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#0F2D42",
  lineHeight: 1.5,
};

const fieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "18px",
};

const FacilityInfo = ({ facilityData }) => {
  if (!facilityData) {
    return (
      <div className="facv-page">
        <div className="facv-card facv-empty">No facility data available.</div>
      </div>
    );
  }

  const isActive = facilityData.status === true;
  const operationalDate = facilityData.operational_date || facilityData.open_date;

  return (
    <div className="facv-page">
      <div className="facv-card facv-card-body" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* ── Basic Information ── */}
        <div>
          <div style={sectionTitleStyle}>Basic Information</div>
          <div style={fieldGridStyle}>
            <div>
              <label style={labelStyle}>
                <Building2 size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Facility Name
              </label>
              <div style={valueStyle}>{facilityData.name || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>Facility Type</label>
              <div style={valueStyle}>{facilityData.facility_type || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>Contact Person</label>
              <div style={valueStyle}>{facilityData.contact_list || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  fontSize: "10.5px",
                  fontWeight: 700,
                  padding: "3px 10px",
                  borderRadius: "999px",
                  background: isActive ? "#E8F5E9" : "#F1F5F9",
                  color: isActive ? "#2E7D32" : "#64748B",
                  border: `1px solid ${isActive ? "#A5D6A7" : "#E2E8F0"}`,
                }}
              >
                {isActive ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                {facilityData.status === true || facilityData.status === false ? (isActive ? "Active" : "Inactive") : "NA"}
              </div>
            </div>
          </div>
        </div>

        {/* ── Location ── */}
        <div>
          <div style={sectionTitleStyle}>Location</div>
          <div style={fieldGridStyle}>
            <div>
              <label style={labelStyle}>
                <MapPin size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                District
              </label>
              <div style={valueStyle}>{facilityData.dist_name || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <MapPin size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Block
              </label>
              <div style={valueStyle}>{facilityData.block_name || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>Latitude</label>
              <div style={valueStyle}>{facilityData.latitude ?? "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>Longitude</label>
              <div style={valueStyle}>{facilityData.longitude ?? "NA"}</div>
            </div>
          </div>
        </div>

        {/* ── Timeline ── */}
        <div>
          <div style={sectionTitleStyle}>Timeline</div>
          <div style={fieldGridStyle}>
            {operationalDate && (
              <div>
                <label style={labelStyle}>
                  <Clock size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                  Operational Date
                </label>
                <div style={valueStyle}>
                  <Moment format="DD-MM-YYYY">{operationalDate}</Moment>
                </div>
              </div>
            )}
            <div>
              <label style={labelStyle}>
                <Clock size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Created On
              </label>
              <div style={valueStyle}>
                {facilityData.created_on ? (
                  <Moment format="DD-MM-YYYY, hh:mm A">{facilityData.created_on}</Moment>
                ) : (
                  "NA"
                )}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Created By</label>
              <div style={valueStyle}>{facilityData.created_by || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Clock size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Updated On
              </label>
              <div style={valueStyle}>
                {facilityData.updated_on ? (
                  <Moment format="DD-MM-YYYY, hh:mm A">{facilityData.updated_on}</Moment>
                ) : (
                  "NA"
                )}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Updated By</label>
              <div style={valueStyle}>{facilityData.updated_by || "NA"}</div>
            </div>
          </div>
        </div>

        {/* ── Address ── */}
        <div>
          <div style={sectionTitleStyle}>Address</div>
          <div
            style={{
              ...valueStyle,
              fontWeight: 500,
              background: "#EBF4FA",
              border: "1px solid #E2E8F0",
              borderRadius: "10px",
              padding: "12px 14px",
            }}
          >
            {facilityData.address || "NA"}
          </div>
        </div>

        {/* ── Description ── */}
        <div>
          <div style={sectionTitleStyle}>Description</div>
          <div
            style={{
              ...valueStyle,
              fontWeight: 500,
              background: "#EBF4FA",
              border: "1px solid #E2E8F0",
              borderRadius: "10px",
              padding: "12px 14px",
            }}
          >
            {facilityData.description || "NA"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilityInfo;
